import { ReviewModalItem } from "@/components/display/ReviewModalItem";
import { HacsOmUploadData } from "@/features/hacs";
import { AnaHazardId } from "@/store/services/facades/types";
import { useGetCurrentGroupIdQuery, useGetGroupsQuery, useGetToolUsageQuery } from "@/store/services/supabase";
import { LargeSiteAdvancedSettings, LargeSiteAdvancedSettingsRefs } from "@/tools/create/lss-from-map/components/LargeSiteAdvancedSettings";
import { LargeSiteAdvancedSettingValues } from "@/tools/create/lss-from-map/types";
import { formatMetresSquaredAsKmSquared } from "@/tools/create/lss-from-map/utils";
import { useCanCreatePrivateLss } from "@/utils/hooks/useCanCreatePrivate";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { ArrowUpOutlined, LockOutlined, PaperClipOutlined, TeamOutlined } from "@ant-design/icons";
import * as turf from "@turf/turf";
import { Alert, Button, Input, Progress, Radio, Select, Statistic } from "antd";
import classnames from "classnames";
import type { Polygon } from "leaflet";
import Link from "next/link";
import { MutableRefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { Components } from "react-markdown";
import s from "../../../aggregate/portfolio-export/components/PortfolioExportUploadReview.module.scss";
const GridItem = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={classnames("grid grid-cols-3 items-center", className)} data-sentry-component="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">{children}</div>;
export interface LargeSiteScreenUploadReviewProps {
  /** Handles whenever a CSV is selected. */
  setData: (data?: HacsOmUploadData) => void;
  /** File data for the chosen CSV upload. */
  data: HacsOmUploadData;
  onContinue: () => void;
  polygons: ReturnType<Polygon["toGeoJSON"]>[];
  setRiskiestPoint: (data: {
    hazard: AnaHazardId | "total";
    year: number;
  }) => void;
  defaultAdvancedSettingsOpen?: boolean;
  onAdvancedSettingsRefs?: (refs: LargeSiteAdvancedSettingsRefs) => void;
  refs?: {
    reviewSection?: MutableRefObject<null>;
    usageSection?: MutableRefObject<null>;
    runAnalysisBtn?: MutableRefObject<null>;
  };
}
export const LargeSiteScreenUploadReview = ({
  data,
  setData,
  setRiskiestPoint,
  onContinue,
  polygons,
  defaultAdvancedSettingsOpen,
  onAdvancedSettingsRefs,
  refs
}: LargeSiteScreenUploadReviewProps) => {
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  const {
    data: groups
  } = useGetGroupsQuery();
  const {
    data: currentGroupId
  } = useGetCurrentGroupIdQuery();
  const isOwner = useIsOwner();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.largeSite.uploadLimitAlert"
  });
  const canCreatePrivate = useCanCreatePrivateLss();
  const [filename, setFilename] = useState(removeFileExtension(data.file.name || ""));
  const [isPrivate, setIsPrivate] = useState(true);
  const [disablePrivateToggle, setDisablePrivateToggle] = useState(true);
  const [keys] = useKeys();
  const [groupId, setGroupId] = useState("");
  const [totalPolygonArea, setTotalPolygonArea] = useState(0);
  const [advancedSettings, setAdvancedSettings] = useState<LargeSiteAdvancedSettingValues>({
    riskiestPoint: {
      year: 2050,
      hazard: "total"
    },
    anomalyManagementRp: true,
    defences: true
  });
  useEffect(() => {
    if (currentGroupId?.id) {
      setGroupId(currentGroupId.id);
    }
  }, [currentGroupId]);
  useEffect(() => {
    if (!canCreatePrivate) {
      setIsPrivate(false);
      setDisablePrivateToggle(true);
    } else if (keys.orgSecret) {
      setIsPrivate(false);
      setDisablePrivateToggle(false);
    }
  }, [canCreatePrivate, keys]);
  useEffect(() => {
    try {
      setTotalPolygonArea(polygons.reduce<number>((total, polygon) => {
        return total + turf.area(polygon);
      }, 0));
    } catch {
      setTotalPolygonArea(0);
    }
  }, [polygons]);
  if (!toolUsage) return <></>;
  const currentUsage = toolUsage.largeSite;
  const quota = currentUsage.quota;
  const usage = currentUsage.usage;

  // Don't count the header row.
  const uploadCount = data.lineCount - 1;
  const uploadPct = parseFloat((1 / quota * 100).toFixed(2));
  const futureUsage = usage + 1;
  const futureUsagePct = parseFloat((futureUsage / quota * 100).toFixed(2));
  const usageStat = `${futureUsage.toLocaleString()} / ${quota.toLocaleString()} assets (${futureUsagePct}%)`;
  const usageAfterUploadStat = `${uploadCount.toLocaleString()} (${uploadPct}%)`;
  const exceedsQuotaBuffer = parseFloat(futureUsagePct.toFixed(2)) > currentUsage.hardLimitPercent;
  const quotaRemaining = quota - usage;
  const showWarning = quotaRemaining === 1;
  const showError = futureUsagePct > 100;
  const handleContinue = () => {
    setData({
      ...data,
      name: filename,
      groupId: isPrivate && currentGroupId ? currentGroupId.id : groupId,
      isPrivate,
      anomalyManagementRp: advancedSettings.anomalyManagementRp,
      defences: advancedSettings.defences
    });
    setRiskiestPoint({
      ...advancedSettings.riskiestPoint
    });
    onContinue();
  };
  const groupOpts = groups && groups.map(g => ({
    key: g.id,
    value: g.id,
    label: g.name || ""
  }));
  const components: Components = {
    a: props => {
      return <Link href={props.href || ""} target="_blank">
          {props.children}
        </Link>;
    }
  };
  return <div className="flex flex-col gap-4" data-sentry-component="LargeSiteScreenUploadReview" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
      <ReviewModalItem ref={refs?.reviewSection} title="Review Upload" data-sentry-element="ReviewModalItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
        <GridItem data-sentry-element="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          <div>Name</div>
          <div className="col-span-2">
            <Input className={s.fileInfoName} value={filename} onChange={val => setFilename(val.target.value)} prefix={<PaperClipOutlined />} data-sentry-element="Input" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>
        </GridItem>

        <GridItem data-sentry-element="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          <div>Total Polygon Area</div>
          <Statistic value={formatMetresSquaredAsKmSquared(totalPolygonArea)} className="col-span-2 *:!text-sm" data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
        </GridItem>

        <GridItem data-sentry-element="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          <div>Total Grid Points</div>
          <Statistic value={uploadCount} className="col-span-2 *:!text-sm" data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
        </GridItem>

        {canCreatePrivate && <GridItem className="pt-8">
            <div>Permissions</div>
            <div className="col-span-2 flex items-center justify-start gap-2">
              <Radio.Group value={isPrivate} disabled={disablePrivateToggle} onChange={e => setIsPrivate(e.target.value)} optionType="button" buttonStyle="solid">
                <Radio.Button value={false}>
                  <div className="flex items-center gap-1">
                    <TeamOutlined />
                    <span>Shared</span>
                  </div>
                </Radio.Button>
                <Radio.Button value={true}>
                  <div className="flex items-center gap-1">
                    <LockOutlined />
                    <span>Private</span>
                  </div>
                </Radio.Button>
              </Radio.Group>
            </div>
          </GridItem>}

        <GridItem className={`${isPrivate ? "opacity-40" : ""}`} data-sentry-element="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          <div>Share with</div>
          <Select className={`col-span-2 col-start-2`} placeholder="Select group..." options={groupOpts} onChange={e => setGroupId(e)} disabled={isPrivate} value={groupId} data-sentry-element="Select" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
        </GridItem>

        <GridItem data-sentry-element="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          <div className="col-span-2 col-start-2 text-xs text-gray-500">
            {isPrivate ? "This portfolio will be only visible to you." : "This portfolio will be visible to everyone in the group."}
          </div>
        </GridItem>
      </ReviewModalItem>

      <LargeSiteAdvancedSettings defaultOpen={defaultAdvancedSettingsOpen} values={advancedSettings} setValues={setAdvancedSettings} setRefs={onAdvancedSettingsRefs} data-sentry-element="LargeSiteAdvancedSettings" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />

      <ReviewModalItem ref={refs?.usageSection} title="Usage Confirmation" data-sentry-element="ReviewModalItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
        <GridItem className="!items-start" data-sentry-element="GridItem" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          <div>Usage after upload</div>

          <div className="col-span-2 flex w-full flex-row justify-between gap-1 pr-2">
            <div className="col-span-2 flex flex-col gap-1">
              <Statistic value={usageStat} className="*:!text-sm" data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
              <Statistic value={usageAfterUploadStat} className="*:!text-sm *:!text-gray-400" suffix={<ArrowUpOutlined />} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
            </div>
            <Progress type="circle" percent={futureUsagePct} strokeWidth={16} size={42} success={{
            percent: currentUsage.usagePercent,
            strokeColor: "#cdcdcd"
          }} strokeColor={"var(--color-accent)"} showInfo={false} style={{
            gridColumn: "2 / 4"
          }} className="max-w-8" data-sentry-element="Progress" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>
        </GridItem>
      </ReviewModalItem>

      {showWarning && <Alert message={<Markdown components={components}>{isOwner ? t("owner.warning") : t("user.warning")}</Markdown>} type="warning" showIcon className={s.alert} />}

      {showError && <Alert message={<div>
              <div>
                <strong>Limit Reached</strong>
              </div>
              {<Markdown components={components}>{isOwner ? t("owner.error") : t("user.error")}</Markdown>}
            </div>} type="error" showIcon className={s.alert} />}

      <div ref={refs?.runAnalysisBtn} className={s.actionButtons}>
        <Button type="primary" onClick={handleContinue} disabled={currentUsage.alwaysEnabled ? false : currentUsage.enabled ? exceedsQuotaBuffer : true} data-sentry-element="Button" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          Run Analysis
        </Button>
      </div>
    </div>;
};